import React from 'react';
import styled from 'styled-components';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Facebook, 
  Twitter, 
  Instagram, 
  Linkedin, 
  Github,
  MessageSquare,
  ExternalLink,
  Shield,
  Award,
  Heart,
  GitCommit,
  Calendar,
  User
} from 'lucide-react';
import { colors, spacing, typography, borderRadius, breakpoints } from '../styles/theme';
import { useTranslations } from '../locales/useI18n';
import { getBuildMetadata, getCommitUrl, formatCommitDate, isDevelopmentBuild } from '../utils/buildMetadata';
import { useAnalytics } from '../hooks/useAnalytics';
import config from '../config';

const FooterContainer = styled.footer`
  background: linear-gradient(135deg, #001529 0%, #0f1419 100%);
  color: ${colors.text.inverse};
  margin-top: auto;
`;

const FooterContent = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: ${spacing[12]} ${spacing[4]} ${spacing[6]};
  
  @media (min-width: ${breakpoints.sm}) {
    padding: ${spacing[12]} ${spacing[6]} ${spacing[6]};
  }
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing[8]};
  
  @media (min-width: ${breakpoints.md}) {
    grid-template-columns: 1fr 1fr;
    gap: ${spacing[10]};
  }
  
  @media (min-width: ${breakpoints.lg}) {
    grid-template-columns: 1.5fr 1fr 1fr 1fr;
    gap: ${spacing[8]};
  }
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
`;

const CompanySection = styled(FooterSection)`
  @media (min-width: ${breakpoints.lg}) {
    grid-column: span 1;
  }
`;

const SectionTitle = styled.h3`
  font-size: ${typography.fontSizes.lg};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.inverse};
  margin: 0 0 ${spacing[4]} 0;
  border-bottom: 2px solid ${colors.primary[500]};
  padding-bottom: ${spacing[2]};
  display: inline-block;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  margin-bottom: ${spacing[4]};
`;

const LogoImage = styled.img`
  height: 40px;
  width: auto;
`;

const LogoText = styled.span`
  font-size: ${typography.fontSizes.xl};
  font-weight: ${typography.fontWeights.bold};
  color: ${colors.text.inverse};
`;

const CompanyDescription = styled.p`
  font-size: ${typography.fontSizes.sm};
  line-height: 1.6;
  color: ${colors.text.inverse};
  opacity: 0.9;
  margin: 0;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.inverse};
  opacity: 0.9;
  
  svg {
    width: 16px;
    height: 16px;
    color: ${colors.primary[400]};
  }
`;

const FooterLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

const FooterLink = styled.a`
  color: ${colors.text.inverse};
  text-decoration: none;
  font-size: ${typography.fontSizes.sm};
  opacity: 0.9;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  
  &:hover {
    opacity: 1;
    color: ${colors.primary[400]};
    transform: translateX(4px);
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${spacing[4]};
  margin-top: ${spacing[4]};
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: ${borderRadius.full};
  color: ${colors.text.inverse};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${colors.primary[500]};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const TrustSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
  margin-top: ${spacing[6]};
`;

const TrustBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[4]};
  align-items: center;
`;

const TrustBadge = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[2]} ${spacing[3]};
  background: rgba(255, 255, 255, 0.1);
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.inverse};
  opacity: 0.9;
  
  svg {
    width: 16px;
    height: 16px;
    color: ${colors.success[400]};
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: ${spacing[8]};
  padding-top: ${spacing[6]};
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
  
  @media (min-width: ${breakpoints.md}) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: ${spacing[6]};
  }
`;

const LegalLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[6]};
`;

const LegalLink = styled.a`
  color: ${colors.text.inverse};
  text-decoration: none;
  font-size: ${typography.fontSizes.xs};
  opacity: 0.7;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 1;
  }
`;

const Copyright = styled.div`
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.inverse};
  opacity: 0.7;
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  
  @media (min-width: ${breakpoints.md}) {
    order: -1;
  }
`;

const BuildMetadata = styled.div`
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.inverse};
  opacity: 0.5;
  margin-top: ${spacing[2]};
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  
  @media (min-width: ${breakpoints.sm}) {
    flex-direction: row;
    gap: ${spacing[4]};
    align-items: center;
  }
`;

const MetadataItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: ${typography.fontSizes.xs};
  
  svg {
    width: 12px;
    height: 12px;
    opacity: 0.7;
  }
`;

const CommitLink = styled.a`
  color: inherit;
  text-decoration: none;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, ${colors.primary[600]} 0%, ${colors.primary[500]} 100%);
  padding: ${spacing[6]} ${spacing[4]};
  text-align: center;
  
  @media (min-width: ${breakpoints.sm}) {
    padding: ${spacing[6]} ${spacing[6]};
  }
`;

const CTAContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const CTATitle = styled.h3`
  font-size: ${typography.fontSizes['2xl']};
  font-weight: ${typography.fontWeights.bold};
  color: ${colors.text.inverse};
  margin: 0 0 ${spacing[3]} 0;
`;

const CTADescription = styled.p`
  font-size: ${typography.fontSizes.lg};
  color: ${colors.text.inverse};
  opacity: 0.9;
  margin: 0 0 ${spacing[6]} 0;
  line-height: 1.6;
`;

const CTAButton = styled.button`
  background: ${colors.white};
  color: ${colors.primary[600]};
  border: none;
  padding: ${spacing[4]} ${spacing[8]};
  border-radius: ${borderRadius.full};
  font-size: ${typography.fontSizes.lg};
  font-weight: ${typography.fontWeights.semibold};
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  }
`;

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslations();
  const { trackEvent } = useAnalytics();
  const buildMetadata = getBuildMetadata();
  const isDev = isDevelopmentBuild(buildMetadata);

  return (
    <FooterContainer>
      <CTASection>
        <CTAContent>
          <CTATitle>{t('footer.ctaTitle')}</CTATitle>
          <CTADescription>
            {t('footer.ctaDescription')}
          </CTADescription>
          <CTAButton onClick={() => {
            trackEvent({ category: 'navigation', action: 'feedback_click', custom_parameters: { action_location: 'footer_cta', destination: 'feedback_form' } });
            window.open(config.app.feedbackFormUrl, '_blank', 'noopener,noreferrer');
          }}>
            <MessageSquare size={20} />
            {t('footer.ctaButton')}
          </CTAButton>
        </CTAContent>
      </CTASection>
      
      <FooterContent>
        <FooterGrid>
          <CompanySection>
            <Logo>
              <LogoImage src="/EFlogo.png" alt={t('footer.companyName')} />
              <LogoText>{t('footer.companyName')}</LogoText>
            </Logo>
            <CompanyDescription>
              {t('footer.companyDescription')}
            </CompanyDescription>
            
            <ContactInfo>
              <ContactItem>
                <Mail size={16} />
                <span>{t('footer.contactEmail')}</span>
              </ContactItem>
              <ContactItem>
                <Phone size={16} />
                <span>{t('footer.contactPhone')}</span>
              </ContactItem>
              <ContactItem>
                <MapPin size={16} />
                <span>{t('footer.contactAddress')}</span>
              </ContactItem>
            </ContactInfo>
            
            <SocialLinks>
              <SocialLink href="#" aria-label="Facebook">
                <Facebook />
              </SocialLink>
              <SocialLink href="#" aria-label="Twitter">
                <Twitter />
              </SocialLink>
              <SocialLink href="#" aria-label="Instagram">
                <Instagram />
              </SocialLink>
              <SocialLink href="#" aria-label="LinkedIn">
                <Linkedin />
              </SocialLink>
              <SocialLink href="#" aria-label="GitHub">
                <Github />
              </SocialLink>
            </SocialLinks>
          </CompanySection>

          <FooterSection>
            <SectionTitle>{t('footer.quickLinksTitle')}</SectionTitle>
            <FooterLinks>
              <FooterLink href="/about">{t('footer.aboutUs')}</FooterLink>
              <FooterLink href="/services">{t('footer.ourServices')}</FooterLink>
              <FooterLink href="/latest-updates">{t('footer.latestUpdates')}</FooterLink>
              <FooterLink href="/careers">
                {t('footer.careers')}
                <ExternalLink size={14} />
              </FooterLink>
              <FooterLink href="/help">{t('footer.helpCenter')}</FooterLink>
              <FooterLink href="/contact">{t('footer.contact')}</FooterLink>
            </FooterLinks>
          </FooterSection>

          <FooterSection>
            <SectionTitle>{t('footer.platformTitle')}</SectionTitle>
            <FooterLinks>
              <FooterLink href="/events">{t('footer.browseEvents')}</FooterLink>
              <FooterLink href="/search">{t('footer.advancedSearch')}</FooterLink>
              <FooterLink href="/api">{t('footer.apiDocumentation')}</FooterLink>
              <FooterLink href="/mobile">{t('footer.mobileApp')}</FooterLink>
              <FooterLink href="/sitemap">{t('footer.siteMap')}</FooterLink>
              <FooterLink href="/statistics">{t('footer.siteStatistics')}</FooterLink>
            </FooterLinks>
          </FooterSection>

          <FooterSection>
            <SectionTitle>{t('footer.trustSafetyTitle')}</SectionTitle>
            <FooterLinks>
              <FooterLink href="/security">
                <Shield size={14} />
                {t('footer.securityCenter')}
              </FooterLink>
              <FooterLink href="/accessibility">{t('footer.accessibility')}</FooterLink>
              <FooterLink href="/community">{t('footer.communityGuidelines')}</FooterLink>
              <FooterLink href="/trust">{t('footer.trustSafety')}</FooterLink>
              <FooterLink href="/feedback">{t('footer.reportIssue')}</FooterLink>
            </FooterLinks>
            
            <TrustSection>
              <TrustBadges>
                <TrustBadge>
                  <Shield size={16} />
                  {t('footer.sslSecured')}
                </TrustBadge>
                <TrustBadge>
                  <Award size={16} />
                  {t('footer.iso27001')}
                </TrustBadge>
                <TrustBadge>
                  <Heart size={16} />
                  {t('footer.gdprCompliant')}
                </TrustBadge>
              </TrustBadges>
            </TrustSection>
          </FooterSection>
        </FooterGrid>

        <FooterBottom>
          <Copyright>
            <span>{t('footer.copyright')} {currentYear} {t('footer.companyName')}. {t('footer.allRightsReserved')}.</span>
          </Copyright>
          
          <LegalLinks>
            <LegalLink href="/privacy">{t('footer.privacyPolicy')}</LegalLink>
            <LegalLink href="/terms">{t('footer.termsOfService')}</LegalLink>
            <LegalLink href="/cookies">{t('footer.cookiePolicy')}</LegalLink>
            <LegalLink href="/disclaimer">{t('footer.disclaimer')}</LegalLink>
          </LegalLinks>
          
          <BuildMetadata>
            <MetadataItem>
              <GitCommit size={12} />
              <CommitLink 
                href={getCommitUrl(buildMetadata)} 
                target="_blank" 
                rel="noopener noreferrer"
                title={`Commit: ${buildMetadata.commitMessage}`}
              >
                {buildMetadata.commitHashShort}
              </CommitLink>
            </MetadataItem>
            <MetadataItem>
              <User size={12} />
              <span title={buildMetadata.commitEmail}>
                {buildMetadata.commitAuthor}
              </span>
            </MetadataItem>
            <MetadataItem>
              <Calendar size={12} />
              <span title={`Built: ${buildMetadata.buildDate}`}>
                {formatCommitDate(buildMetadata)}
              </span>
            </MetadataItem>
            {isDev && (
              <MetadataItem>
                <span>🚧 Development</span>
              </MetadataItem>
            )}
          </BuildMetadata>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;